/**
 * LoadingState — Generic animated progress card used by all three modules.
 */
import { useEffect, useRef, useState } from 'react'
import type { PipelineStep } from '@/types'
import StepList from './StepList'

interface LoadingStateProps {
  jobId:       string
  step:        string
  progress:    number
  title:       string
  subtitle?:   string
  steps:       PipelineStep[]
  onCancel:    () => void
}

function Spinner() {
  return (
    <svg className="animate-spin text-brand flex-shrink-0"
      width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.15" strokeWidth="2.5"/>
      <path d="M12 3a9 9 0 0 1 9 9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
    </svg>
  )
}

function fmtElapsed(secs: number): string {
  if (secs < 60) return `${secs}s`
  return `${Math.floor(secs / 60)}m ${secs % 60}s`
}

export default function LoadingState({
  step, progress, title, subtitle, steps, onCancel,
}: LoadingStateProps) {
  const [displayPct, setDisplayPct] = useState(progress || 2)
  const [elapsed,    setElapsed]    = useState(0)
  const startRef = useRef(Date.now())

  useEffect(() => {
    setDisplayPct(prev => Math.max(prev, progress))
  }, [progress])

  useEffect(() => {
    const id = setInterval(() => {
      setDisplayPct(prev => {
        const ceiling = Math.min(progress + 8, 97)
        return prev < ceiling ? prev + 0.4 : prev
      })
    }, 1000)
    return () => clearInterval(id)
  }, [progress])

  // Elapsed timer
  useEffect(() => {
    const id = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startRef.current) / 1000))
    }, 1000)
    return () => clearInterval(id)
  }, [])

  const isQueued = step === 'queued' || step === 'starting'
  const stuckMsg = isQueued && elapsed > 20
    ? 'Waiting for a worker to pick up the job…'
    : null

  return (
    <div className="flex flex-col items-center justify-start pt-16 pb-24 px-5 animate-fade-up">
      <div className="w-full max-w-md">
        <div className="bg-white border border-slate-100 rounded-2xl shadow-card p-8">
          {/* Spinner + title */}
          <div className="flex items-center gap-3 mb-6">
            <Spinner />
            <div className="flex-1 min-w-0">
              <h2 className="text-base font-semibold text-slate-900">{title}</h2>
              {subtitle && (
                <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>
              )}
            </div>
            {/* Elapsed time */}
            <span className="text-xs text-slate-400 tabular-nums flex-shrink-0">
              {fmtElapsed(elapsed)}
            </span>
          </div>

          {/* Queued hint */}
          {stuckMsg && (
            <div className="mb-4 rounded-lg bg-amber-50 border border-amber-100 px-3 py-2">
              <p className="text-xs text-amber-700">{stuckMsg}</p>
            </div>
          )}

          {/* Step list */}
          <div className="mb-6">
            <StepList steps={steps} activeStep={step} />
          </div>

          {/* Progress bar */}
          <div className="bg-slate-100 rounded-full h-1.5 overflow-hidden" role="progressbar"
               aria-valuenow={Math.round(displayPct)} aria-valuemin={0} aria-valuemax={100}>
            <div
              className="h-full bg-gradient-to-r from-brand to-blue-400 rounded-full progress-bar-fill"
              style={{ width: `${displayPct}%` }}
            />
          </div>
          <p className="text-right text-xs text-slate-400 mt-1.5 tabular-nums">
            {Math.round(displayPct)}%
          </p>
        </div>

        <div className="mt-5 flex justify-center">
          <button
            onClick={onCancel}
            className="text-sm text-slate-400 hover:text-slate-600 transition-colors
                       focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand rounded px-2 py-1"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}
